/**
 * Pure selectors (tech-design §2.1: "everything derived is a selector, never
 * stored"). Given a {@link GameState} and {@link Content} they compute costs,
 * rates, unlock visibility, breakthrough odds and prestige gain — no mutation, no
 * randomness, no time.
 */
import {
  ONE,
  ZERO,
  add,
  div,
  gte,
  isZero,
  lte,
  max as numMax,
  mul,
  num,
  pow,
  sub,
  toNumber,
  type Num,
  type NumSource,
} from './num.js';
import type { Content } from './content.js';
import {
  getBreakthrough,
  getGenerator,
  getPrestige,
  getResource,
  hasMilestone,
  hasTrait,
  hasUpgrade,
  isAgeCompleted,
  type GameState,
} from './state.js';
import type { Age, Breakthrough, Curve, Effect } from '@content/index.js';

// ── Curves (content-schema §4) ────────────────────────────────────────────────

/**
 * Evaluate a cost/growth curve at index `n`. For generator costs `n` is the owned
 * count (the next unit is the `(n+1)`th); for prestige gain `n` is the metric.
 */
export function evalCurve(curve: Curve, n: NumSource): Num {
  switch (curve.type) {
    case 'constant':
      return num(curve.value);
    case 'linear':
      return add(num(curve.base), mul(curve.step, n));
    case 'exponential':
      return mul(num(curve.base), pow(curve.growth, toNumber(n)));
    case 'polynomial':
      return mul(num(curve.base), pow(n, curve.exponent));
  }
}

// ── Effects (content-schema §5) ───────────────────────────────────────────────

interface ParsedTarget {
  readonly kind: string;
  readonly id: string;
  readonly prop: string;
}

/** Split `<kind>.<id…>.<prop>` — id may contain dots, so it is the middle. */
export function parseTarget(target: string): ParsedTarget | null {
  const parts = target.split('.');
  if (parts.length < 2) return null;
  const kind = parts[0];
  const prop = parts[parts.length - 1];
  if (kind === undefined || prop === undefined) return null;
  return { kind, id: parts.slice(1, -1).join('.'), prop };
}

/** Aggregated effect operands for the fixed `add → mul → pow` application order. */
interface Mods {
  readonly add: Num;
  readonly mul: Num;
  /** Product of pow values (identity 1) — applied once as a single exponentiation. */
  readonly powProd: number;
}

/** Every effect currently active: from owned upgrades, traits, milestones, prestige. */
export function collectActiveEffects(state: GameState, content: Content): Effect[] {
  const effects: Effect[] = [];
  for (const id of state.upgrades)
    effects.push(...(content.upgrades.get(id)?.effects ?? []));
  for (const id of state.traits) effects.push(...(content.traits.get(id)?.effects ?? []));
  for (const id of state.milestones)
    effects.push(...(content.milestones.get(id)?.effects ?? []));
  for (const [layerId, layer] of content.prestige) {
    const purchased = getPrestige(state, layerId).purchased;
    if (purchased.length === 0) continue;
    for (const upgrade of layer.tree) {
      if (purchased.includes(upgrade.id)) effects.push(...upgrade.effects);
    }
  }
  return effects;
}

/** Reduce the effects whose target matches `(kind, id, prop)` into {@link Mods}. */
function modsFor(effects: readonly Effect[], match: (t: ParsedTarget) => boolean): Mods {
  let addend = ZERO;
  let factor = ONE;
  let powProd = 1;
  for (const effect of effects) {
    const parsed = parseTarget(effect.target);
    if (parsed === null || !match(parsed)) continue;
    switch (effect.op) {
      case 'add':
        addend = add(addend, effect.value);
        break;
      case 'mul':
        factor = mul(factor, effect.value);
        break;
      case 'pow':
        powProd *= Number(effect.value);
        break;
    }
  }
  return { add: addend, mul: factor, powProd };
}

/** Apply mods in the canonical order: `((base + add) * mul) ^ powProd`. */
function applyMods(base: NumSource, mods: Mods): Num {
  const summed = add(base, mods.add);
  const scaled = mul(summed, mods.mul);
  return mods.powProd === 1 ? scaled : pow(scaled, mods.powProd);
}

// ── Generators ────────────────────────────────────────────────────────────────

/**
 * A generator's production multiplier: base 1, boosted by effects targeting its
 * `rate` and by any `age.<age>.allProduction` effect for the age it belongs to.
 */
export function generatorMultiplier(
  state: GameState,
  content: Content,
  genId: string,
): Num {
  const def = content.generators.get(genId);
  const effects = collectActiveEffects(state, content);
  const ageId = def?.age;
  const mods = modsFor(
    effects,
    (t) =>
      (t.kind === 'generator' && t.id === genId && t.prop === 'rate') ||
      (t.kind === 'age' &&
        ageId !== undefined &&
        t.id === ageId &&
        t.prop === 'allProduction') ||
      // Universe-wide multiplier (GAM-11 Essence tree): every generator, every age.
      (t.kind === 'global' && t.prop === 'allProduction'),
  );
  return applyMods(ONE, mods);
}

/** Cost multiplier/addend applied to a generator's raw curve (effects on `cost`). */
function generatorCostMods(state: GameState, content: Content, genId: string): Mods {
  return modsFor(
    collectActiveEffects(state, content),
    (t) => t.kind === 'generator' && t.id === genId && t.prop === 'cost',
  );
}

/** The resource and amount required to buy the next unit of a generator. */
export function generatorCost(
  state: GameState,
  content: Content,
  genId: string,
): { readonly resource: string; readonly amount: Num } {
  const def = content.generators.get(genId);
  if (def === undefined) return { resource: '', amount: ZERO };
  const owned = getGenerator(state, genId).owned;
  const base = evalCurve(def.cost.curve, owned);
  const amount = applyMods(base, generatorCostMods(state, content, genId));
  return { resource: def.cost.resource, amount: numMax(amount, ZERO) };
}

/** Closed-form Σ of the raw curve over `[from, from+count)` (no effect mods). */
function cumulativeCurve(curve: Curve, from: number, count: number): Num {
  if (count <= 0) return ZERO;
  switch (curve.type) {
    case 'constant':
      return mul(curve.value, count);
    case 'linear': {
      // Σ base + step*(from+i), i in [0,count) = count*base + step*(count*from + count*(count-1)/2)
      const indices = count * from + (count * (count - 1)) / 2;
      return add(mul(curve.base, count), mul(curve.step, indices));
    }
    case 'exponential': {
      const first = mul(num(curve.base), pow(curve.growth, from));
      if (curve.growth === 1) return mul(first, count);
      // geometric series: first * (growth^count - 1) / (growth - 1)
      const ratio = div(add(pow(curve.growth, count), -1), curve.growth - 1);
      return mul(first, ratio);
    }
    case 'polynomial': {
      // No closed form for arbitrary exponent — sum term by term (count is bounded
      // by maxAffordable's search, which itself caps growth).
      let total = ZERO;
      for (let i = 0; i < count; i += 1) {
        total = add(total, mul(num(curve.base), pow(from + i, curve.exponent)));
      }
      return total;
    }
  }
}

/** Total cost to buy `count` units of a generator from its current owned count. */
export function cumulativeGeneratorCost(
  state: GameState,
  content: Content,
  genId: string,
  count: number,
): Num {
  const def = content.generators.get(genId);
  if (def === undefined || count <= 0) return ZERO;
  const owned = getGenerator(state, genId).owned;
  const mods = generatorCostMods(state, content, genId);
  const rawSum = cumulativeCurve(def.cost.curve, owned, count);
  if (mods.powProd === 1) {
    // add is per-unit; mul is uniform → closed form stays exact.
    return numMax(mul(add(rawSum, mul(mods.add, count)), mods.mul), ZERO);
  }
  // Pow on cost is exotic; fall back to per-unit application.
  let total = ZERO;
  for (let i = 0; i < count; i += 1) {
    total = add(total, applyMods(evalCurve(def.cost.curve, owned + i), mods));
  }
  return numMax(total, ZERO);
}

/**
 * How many units of a generator the current resource stock affords. Binary search
 * over `cumulativeGeneratorCost` so `buy max` never overspends, even for huge
 * exponential ladders (no per-unit loop).
 */
export function maxAffordable(state: GameState, content: Content, genId: string): number {
  const def = content.generators.get(genId);
  if (def === undefined) return 0;
  const budget = getResource(state, def.cost.resource).amount;
  const affords = (n: number): boolean =>
    lte(cumulativeGeneratorCost(state, content, genId, n), budget);

  const one = cumulativeGeneratorCost(state, content, genId, 1);
  if (lte(one, ZERO)) return 0; // free/degenerate curve — refuse to loop forever
  if (!affords(1)) return 0; // can't even afford the first unit

  // Grow an upper bound by doubling until it's unaffordable (or hits the safety cap).
  const CAP = 1_000_000;
  let hi = 1;
  while (hi < CAP && affords(hi * 2)) hi *= 2;
  if (hi >= CAP) return CAP;

  // Binary search the boundary in (hi, hi*2].
  let lo = hi;
  let high = hi * 2;
  while (lo < high) {
    const mid = Math.ceil((lo + high) / 2);
    if (affords(mid)) lo = mid;
    else high = mid - 1;
  }
  return lo;
}

// ── Resource passive production (effects on resource.<id>.passive) ─────────────

export function resourcePassiveRatePerSec(
  state: GameState,
  content: Content,
  resourceId: string,
): Num {
  const mods = modsFor(
    collectActiveEffects(state, content),
    (t) => t.kind === 'resource' && t.id === resourceId && t.prop === 'passive',
  );
  return numMax(applyMods(ZERO, mods), ZERO);
}

/**
 * A resource's net rate per second at full throughput: passive effects plus every
 * owned generator's `produces` minus its `consumes` (scaled by owned × cached
 * multiplier, mirroring {@link ../tick}). Converters short of inputs run slower in
 * the tick than this reports — it is the *potential* rate the UI's `/s` readout
 * and the away summary show, and may be negative for a heavily-drained input.
 */
export function resourceRatePerSec(
  state: GameState,
  content: Content,
  resourceId: string,
): Num {
  let rate = resourcePassiveRatePerSec(state, content, resourceId);
  for (const [genId, gen] of Object.entries(state.generators)) {
    if (gen.owned <= 0) continue;
    const def = content.generators.get(genId);
    if (def === undefined) continue;
    const throughput = mul(gen.multiplier, gen.owned);
    for (const output of def.produces) {
      if (output.resource !== resourceId) continue;
      rate = add(rate, mul(output.baseRatePerSec, throughput));
    }
    for (const input of def.consumes ?? []) {
      if (input.resource !== resourceId) continue;
      rate = sub(rate, mul(input.baseRatePerSec, throughput));
    }
  }
  return rate;
}

/** One owned generator/converter's net per-second contribution to a resource. */
export interface ResourceContribution {
  readonly generatorId: string;
  readonly netPerSec: Num;
}

/**
 * Per-generator breakdown of a resource's net rate — the same aggregation as
 * {@link resourceRatePerSec}, itemized instead of summed, for the overlay's
 * resource detail popup. Only owned generators with a non-zero net appear.
 */
export function resourceContributors(
  state: GameState,
  content: Content,
  resourceId: string,
): ResourceContribution[] {
  const rows: ResourceContribution[] = [];
  for (const [genId, gen] of Object.entries(state.generators)) {
    if (gen.owned <= 0) continue;
    const def = content.generators.get(genId);
    if (def === undefined) continue;
    const throughput = mul(gen.multiplier, gen.owned);
    let net = ZERO;
    for (const output of def.produces) {
      if (output.resource !== resourceId) continue;
      net = add(net, mul(output.baseRatePerSec, throughput));
    }
    for (const input of def.consumes ?? []) {
      if (input.resource !== resourceId) continue;
      net = sub(net, mul(input.baseRatePerSec, throughput));
    }
    if (isZero(net)) continue;
    rows.push({ generatorId: genId, netPerSec: net });
  }
  return rows;
}

// ── Offline catch-up cap (effects on offline.cap) ──────────────────────────────

/**
 * The offline catch-up limit in seconds: `baseSec` modified by any id-less
 * `offline.cap` effects (add → mul → pow), so upgrades can extend the base 8 h cap
 * (tech-design §2.4). Never negative.
 */
export function offlineCapSec(
  state: GameState,
  content: Content,
  baseSec: number,
): number {
  const mods = modsFor(
    collectActiveEffects(state, content),
    (t) => t.kind === 'offline' && t.prop === 'cap',
  );
  return Math.max(0, toNumber(applyMods(baseSec, mods)));
}

// ── Unlock conditions (content-schema §6) ──────────────────────────────────────

interface UnlockableCondition {
  readonly unlock?: unknown;
}

export function evalCondition(
  state: GameState,
  content: Content,
  condition: unknown,
): boolean {
  if (typeof condition !== 'object' || condition === null) return true;
  const c = condition as Record<string, unknown>;

  if (Array.isArray(c.all))
    return c.all.every((sub) => evalCondition(state, content, sub));
  if (Array.isArray(c.any))
    return c.any.some((sub) => evalCondition(state, content, sub));
  if ('not' in c) return !evalCondition(state, content, c.not);

  switch (c.type) {
    case 'resourceLifetime':
      return gte(getResource(state, String(c.resource)).lifetime, asNum(c.amount));
    case 'resourceCurrent':
      return gte(getResource(state, String(c.resource)).amount, asNum(c.amount));
    case 'generatorOwned':
      return getGenerator(state, String(c.generator)).owned >= Number(c.count);
    case 'upgradeOwned': {
      const id = String(c.upgrade);
      return (
        hasUpgrade(state, id) ||
        Object.values(state.prestige).some((layer) => layer.purchased.includes(id))
      );
    }
    case 'traitOwned':
      return hasTrait(state, String(c.trait));
    case 'milestone':
      return hasMilestone(state, String(c.milestone));
    case 'breakthroughHits':
      return getBreakthrough(state, String(c.breakthrough)).hits >= Number(c.hits);
    case 'ageCompleted':
      return isAgeCompleted(state, String(c.age));
    case 'prestigeCount':
      return getPrestige(state, String(c.layer)).count >= Number(c.count);
    default:
      return true;
  }
}

const asNum = (v: unknown): Num => num(typeof v === 'string' ? v : Number(v));

/** An entity is unlocked when it has no `unlock` gate or its condition tree holds. */
export function isUnlocked(
  state: GameState,
  content: Content,
  entity: UnlockableCondition | undefined,
): boolean {
  if (entity === undefined) return false;
  if (entity.unlock === undefined) return true;
  return evalCondition(state, content, entity.unlock);
}

// ── Breakthroughs (content-schema §7) ──────────────────────────────────────────

/** Current hit chance in `[0, 1]`: modified base chance plus accumulated pity. */
export function breakthroughChance(
  state: GameState,
  content: Content,
  id: string,
): number {
  const def = content.breakthroughs.get(id);
  if (def === undefined) return 0;
  const mods = modsFor(
    collectActiveEffects(state, content),
    (t) => t.kind === 'breakthrough' && t.id === id && t.prop === 'chance',
  );
  const modified = toNumber(applyMods(def.baseChance, mods));
  const pity = getBreakthrough(state, id).pity;
  const chance = modified + pity;
  return chance < 0 ? 0 : chance > 1 ? 1 : chance;
}

/** Fails after which pity alone guarantees a hit (chance reaches 1), or `null`. */
export function pityBound(def: Breakthrough): number | null {
  const inc = def.pity?.incrementPerFail ?? 0;
  if (inc <= 0) return null;
  return Math.ceil((1 - def.baseChance) / inc);
}

/** Cost of the next attempt: base amounts scaled by the optional per-hit curve. */
export function breakthroughCost(
  state: GameState,
  content: Content,
  id: string,
): { readonly resource: string; readonly amount: Num }[] {
  const def = content.breakthroughs.get(id);
  if (def === undefined) return [];
  const hits = getBreakthrough(state, id).hits;
  const multiplier = def.costCurve ? evalCurve(def.costCurve, hits) : ONE;
  return def.cost.map((c) => ({
    resource: c.resource,
    amount: mul(c.amount, multiplier),
  }));
}

// ── Prestige (content-schema §9) ────────────────────────────────────────────────

/** Currency awarded if the layer were prestiged now (floored, never negative). */
export function prestigeGain(state: GameState, content: Content, layerId: string): Num {
  const layer = content.prestige.get(layerId);
  if (layer === undefined) return ZERO;
  const metric = layer.gain.metric;
  let lifetime = ZERO;
  if (metric.type === 'ageWeighted') {
    // Sum every resource's lifetime scaled by weightBase^(ageOrder-1): pushing one
    // age deeper always beats grinding a cleared one (game-design §5, GAM-11).
    for (const [id, def] of content.resources) {
      const order = content.ages.get(def.age)?.order ?? 1;
      const weight = pow(num(metric.weightBase), order - 1);
      lifetime = add(lifetime, mul(getResource(state, id).lifetime, weight));
    }
  } else if (metric.resource !== undefined) {
    lifetime = getResource(state, metric.resource).lifetime;
  } else if (metric.resourcePerAge !== undefined) {
    const current = state.ages.current;
    const resourceId = current === null ? undefined : metric.resourcePerAge[current];
    if (resourceId !== undefined) lifetime = getResource(state, resourceId).lifetime;
  }
  const gainMods = modsFor(
    collectActiveEffects(state, content),
    (t) => t.kind === 'prestige' && t.id === layerId && t.prop === 'gain',
  );
  const raw = applyMods(evalCurve(layer.gain.curve, lifetime), gainMods);
  return numMax(raw.floor(), ZERO);
}

/** A prestige layer is available once the player has ever reached `unlockAgeOrder`
 * (a lifetime stat, so it survives the layer's own full reset — GAM-11). */
export function isPrestigeUnlocked(
  state: GameState,
  content: Content,
  layerId: string,
): boolean {
  const layer = content.prestige.get(layerId);
  if (layer === undefined) return false;
  if (layer.unlockAgeOrder === undefined) return true;
  return state.meta.maxAgeOrderReached >= layer.unlockAgeOrder;
}

/** Ages ordered by their declared `order` (deterministic age progression). */
export function orderedAges(content: Content): Age[] {
  return [...content.ages.values()].sort((a, b) => a.order - b.order);
}
